import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { createQuery } from "@tanstack/solid-query";
import { firebase } from "@core/firebase/app";
import { queryKeys } from "@core/query";
import { toDate } from "@modules/shared/firestore";
import { mapBookingSummary } from "@modules/bookings/service";
import type { BookingSummary } from "@modules/bookings/types";
import type { UserDetail, UserSummary, UserStats } from "./types";

const USERS_COLLECTION = "users";
const BOOKINGS_COLLECTION = "bookings";
const userSummaryCache = new Map<string, Promise<UserSummary | null>>();

const readString = (value: unknown, fallback: string | null = null): string | null => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  return fallback;
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return null;
};

const mapUserStats = (raw: unknown): UserStats | null => {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const data = raw as Record<string, unknown>;
  const totalCleanings = readNumber(data["totalCleanings"]);
  const totalSquareFootCleaned = readNumber(data["totalSquareFootCleaned"]);
  if (totalCleanings === null && totalSquareFootCleaned === null) {
    return null;
  }
  return {
    totalCleanings,
    totalSquareFootCleaned,
  };
};

const mapUserSummary = (id: string, raw: Record<string, unknown>): UserSummary => {
  const displayName = readString(raw["displayName"], readString(raw["email"], readString(raw["phoneNumber"], id)));
  return {
    id,
    displayName,
    email: readString(raw["email"]),
    phoneNumber: readString(raw["phoneNumber"]),
    createdAt: toDate(raw["createdAt"]),
    lastLoginAt: toDate(raw["lastLoginAt"] ?? raw["lastSeenAt"] ?? null),
    stats: mapUserStats(raw["stats"]),
  };
};

const fetchUserSummary = async (userId: string): Promise<UserSummary | null> => {
  const userRef = doc(firebase.firestore, USERS_COLLECTION, userId);
  const snapshot = await getDoc(userRef);
  if (!snapshot.exists()) {
    return null;
  }
  return mapUserSummary(snapshot.id, snapshot.data() as Record<string, unknown>);
};

export const getUserSummaryCached = async (userId: string): Promise<UserSummary | null> => {
  if (!userId) {
    return null;
  }
  const existing = userSummaryCache.get(userId);
  if (existing) {
    return existing;
  }
  const promise = fetchUserSummary(userId)
    .catch((error) => {
      userSummaryCache.delete(userId);
      throw error;
    })
    .then((result) => {
      if (result === null) {
        userSummaryCache.delete(userId);
      }
      return result;
    });
  userSummaryCache.set(userId, promise);
  return promise;
};

export const clearUserSummaryCache = () => userSummaryCache.clear();

export const useUserSummaryQuery = (userId: () => string | null) =>
  createQuery(() => {
    const id = userId();
    return {
      queryKey: queryKeys.users.summary(id ?? "summary"),
      enabled: Boolean(id),
      queryFn: () => (id ? getUserSummaryCached(id) : Promise.resolve(null)),
    };
  });


const fetchUserBookings = async (userId: string, take = 5): Promise<BookingSummary[]> => {
  const bookingsRef = collection(firebase.firestore, BOOKINGS_COLLECTION);
  const bookingsQuery = query(
    bookingsRef,
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
    limit(take),
  );
  const snapshot = await getDocs(bookingsQuery);
  return snapshot.docs.map((docSnap) => mapBookingSummary(docSnap.id, docSnap.data() as Record<string, unknown>));
};

export const fetchUsers = async (take = 40): Promise<UserSummary[]> => {
  const usersRef = collection(firebase.firestore, USERS_COLLECTION);
  const usersQuery = query(usersRef, limit(take));
  const snapshot = await getDocs(usersQuery);
  return snapshot.docs
    .map((docSnap) => mapUserSummary(docSnap.id, docSnap.data() as Record<string, unknown>))
    .sort((a, b) => {
      const left = (a.displayName ?? a.email ?? a.id).toLowerCase();
      const right = (b.displayName ?? b.email ?? b.id).toLowerCase();
      return left.localeCompare(right);
    });
};

export const fetchUserDetail = async (userId: string): Promise<UserDetail | null> => {
  const user = await fetchUserSummary(userId);
  if (!user) {
    return null;
  }
  const bookings = await fetchUserBookings(user.id);
  return {
    user,
    recentBookings: bookings,
  };
};

export const useUserListQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.users.root(),
    queryFn: () => fetchUsers(),
  }));

export const useUserDetailQuery = (userId: () => string | null) =>
  createQuery(() => {
    const id = userId();
    return {
      queryKey: queryKeys.users.byId(id ?? "detail"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchUserDetail(id) : Promise.resolve(null)),
    };
  });
